import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import {
  PencilSimpleLine, X, ArrowCounterClockwise, FloppyDisk, Eye, EyeSlash,
  DotsSixVertical, Trash, Plus, ArrowsHorizontal, Square,
} from '@phosphor-icons/react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Dashboard-builder side-panel.
 *
 * Lets users:
 *   - Reorder widgets via HTML5 drag-and-drop
 *   - Toggle each widget's visibility
 *   - Resize between Full / Half width (responsive 2-col)
 *   - Add new widget instances from a catalog (incl. multiple
 *     `low_stock_ticker`s scoped to different warehouses)
 *   - Remove widget instances they don't want
 *
 * Each row in `layout` is `{instance_id, widget_id, visible, size, config}`.
 */
export default function DashboardCustomizer({
  layout, catalog, onChange, onSave, onReset, onClose,
}) {
  const [dragIndex, setDragIndex] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [warehouses, setWarehouses] = useState([]);

  // Fetch warehouses once — powers the warehouse dropdown for scope-aware widgets.
  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/api/dashboard/catalog`, { withCredentials: true });
        setWarehouses(data.warehouses || []);
      } catch (_) { /* silent — dropdown just stays empty */ }
    })();
  }, []);

  const swap = (from, to) => {
    if (from === to || from < 0 || to < 0 || from >= layout.length || to >= layout.length) return;
    const next = layout.slice();
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    onChange(next);
  };
  const update = (i, patch) => {
    const next = layout.slice();
    next[i] = { ...next[i], ...patch };
    onChange(next);
  };
  const updateConfig = (i, cfgPatch) => {
    const next = layout.slice();
    next[i] = { ...next[i], config: { ...(next[i].config || {}), ...cfgPatch } };
    onChange(next);
  };
  const remove = (i) => {
    if (layout.length <= 1) {
      toast.error('Keep at least one widget');
      return;
    }
    const next = layout.slice();
    next.splice(i, 1);
    onChange(next);
  };
  const addInstance = (widget) => {
    const instance_id = (crypto.randomUUID?.() || String(Math.random()).slice(2)).replace(/-/g, '');
    onChange([...layout, {
      instance_id, widget_id: widget.id, visible: true, size: 'full', config: {},
    }]);
    setShowAdd(false);
    toast.success(`Added ${widget.label}`);
  };

  const save = async () => {
    try { await onSave?.(); toast.success('Dashboard layout saved'); onClose?.(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
  };
  const reset = async () => {
    if (!window.confirm('Restore the default dashboard layout? Your customisations will be lost.')) return;
    try { await onReset?.(); toast.success('Reset to default'); onClose?.(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Reset failed'); }
  };

  const catalogList = Object.entries(catalog || {}).map(([id, meta]) => ({ id, ...meta }));
  const presentSingles = new Set(
    layout.filter((w) => catalog?.[w.widget_id] && !catalog[w.widget_id].multi)
          .map((w) => w.widget_id)
  );
  const addable = catalogList.filter((w) => w.multi || !presentSingles.has(w.id));

  return (
    <div
      data-testid="dashboard-customizer"
      className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm flex items-start justify-end"
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
    >
      <div className="w-full max-w-md h-full bg-card shadow-2xl border-l border-border flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b-2 border-slate-900">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold">Dashboard</div>
            <h2 className="text-xl font-bold">Customise layout</h2>
          </div>
          <button data-testid="customizer-close" onClick={onClose} className="p-1 hover:bg-muted">
            <X size={18} />
          </button>
        </div>

        <div className="px-5 py-3 text-xs text-muted-foreground border-b border-border bg-muted">
          Drag to reorder · toggle eye to hide · resize between Full/Half · add widgets from the catalog below.
        </div>

        <ul className="flex-1 overflow-auto divide-y divide-slate-100" data-testid="customizer-list">
          {layout.map((w, i) => {
            const meta = (catalog || {})[w.widget_id] || { label: w.widget_id };
            const supportsWarehouse = !!meta.supports_warehouse;
            return (
              <li
                key={w.instance_id}
                data-testid={`customizer-item-${w.widget_id}`}
                draggable
                onDragStart={() => setDragIndex(i)}
                onDragEnd={() => setDragIndex(null)}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => { e.preventDefault(); swap(dragIndex, i); setDragIndex(null); }}
                className={`px-4 py-3 cursor-grab active:cursor-grabbing ${dragIndex === i ? 'opacity-50 bg-muted' : 'hover:bg-muted'}`}
              >
                <div className="flex items-center gap-2">
                  <DotsSixVertical size={18} weight="bold" className="text-slate-300 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className={`text-sm font-bold truncate ${!w.visible ? 'text-muted-foreground line-through' : 'text-foreground'}`}>
                      {meta.label}
                      {w.config?.warehouse_id && (
                        <span className="ml-2 text-[10px] text-muted-foreground font-mono uppercase tracking-widest">
                          {warehouses.find((x) => x.id === w.config.warehouse_id)?.code || 'WH'}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-1 mt-1">
                      <button
                        data-testid={`customizer-size-${w.widget_id}-${i}`}
                        onClick={() => update(i, { size: w.size === 'full' ? 'half' : 'full' })}
                        title={w.size === 'full' ? 'Full width' : 'Half width'}
                        className={`px-1.5 py-0.5 border text-[10px] font-bold uppercase tracking-widest ${
                          w.size === 'full' ? 'border-brand text-brand' : 'border-border text-muted-foreground'
                        }`}
                      >
                        {w.size === 'full'
                          ? <><Square size={10} weight="bold" className="inline mr-1" />Full</>
                          : <><ArrowsHorizontal size={10} weight="bold" className="inline mr-1" />Half</>}
                      </button>
                      {supportsWarehouse && (
                        <select
                          data-testid={`customizer-warehouse-${i}`}
                          value={w.config?.warehouse_id || ''}
                          onChange={(e) => updateConfig(i, { warehouse_id: e.target.value || undefined })}
                          onClick={(e) => e.stopPropagation()}
                          className="text-[10px] border border-border px-1 py-0.5 bg-card max-w-[8rem]"
                        >
                          <option value="">All warehouses</option>
                          {warehouses.map((wh) => (
                            <option key={wh.id} value={wh.id}>{wh.name || wh.code}</option>
                          ))}
                        </select>
                      )}
                    </div>
                  </div>
                  <button
                    data-testid={`customizer-toggle-${w.widget_id}`}
                    onClick={() => update(i, { visible: !w.visible })}
                    title={w.visible ? 'Hide' : 'Show'}
                    className={`p-1.5 border ${w.visible ? 'border-emerald-300 bg-emerald-50 text-emerald-700' : 'border-border bg-card text-muted-foreground'}`}
                  >
                    {w.visible ? <Eye size={14} weight="bold" /> : <EyeSlash size={14} weight="bold" />}
                  </button>
                  <button
                    data-testid={`customizer-remove-${i}`}
                    onClick={() => remove(i)}
                    title="Remove this widget"
                    className="p-1.5 border border-border bg-card text-rose-600 hover:bg-rose-50"
                  >
                    <Trash size={14} weight="bold" />
                  </button>
                </div>
              </li>
            );
          })}
        </ul>

        <div className="relative z-10 px-5 py-3 border-t border-slate-100 bg-card">
          <Button
            data-testid="customizer-add-btn"
            variant="outline"
            onClick={() => setShowAdd((s) => !s)}
            className="w-full rounded-none text-xs"
          >
            <Plus size={14} weight="bold" className="mr-1" /> {showAdd ? 'Cancel' : 'Add widget'}
          </Button>
          {showAdd && (
            <ul data-testid="customizer-catalog" className="mt-2 max-h-40 overflow-auto border border-border">
              {addable.length === 0 && (
                <li className="px-3 py-2 text-xs text-muted-foreground italic">All single-instance widgets already on your dashboard.</li>
              )}
              {addable.map((w) => (
                <li
                  key={w.id}
                  data-testid={`customizer-catalog-item-${w.id}`}
                  onClick={() => addInstance(w)}
                  className="px-3 py-2 text-sm hover:bg-muted cursor-pointer flex items-center justify-between border-b border-border last:border-0"
                >
                  <span className="font-medium">{w.label}</span>
                  {w.multi && <span className="text-[10px] uppercase tracking-widest text-emerald-600">Multi</span>}
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="relative z-10 px-5 py-4 pb-16 border-t border-border flex items-center gap-2 bg-muted">
          <Button data-testid="customizer-reset" variant="outline" onClick={reset} className="rounded-none text-xs">
            <ArrowCounterClockwise size={14} weight="bold" className="mr-1" /> Reset
          </Button>
          <div className="flex-1" />
          <Button data-testid="customizer-cancel" variant="outline" onClick={onClose} className="rounded-none">Cancel</Button>
          <Button
            data-testid="customizer-save"
            onClick={save}
            className="bg-brand hover:bg-brand/90 text-white rounded-none font-bold"
          >
            <FloppyDisk size={14} weight="bold" className="mr-1" /> Save
          </Button>
        </div>
      </div>
    </div>
  );
}

/** Trigger button used in the Dashboard header. */
export function CustomizerTrigger({ onClick }) {
  return (
    <Button
      data-testid="dashboard-customize-btn"
      onClick={onClick}
      variant="outline"
      className="text-xs"
    >
      <PencilSimpleLine size={14} weight="bold" className="mr-1" /> Customise
    </Button>
  );
}
